import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Container, Typography, Card, Stack, Grid, 
  Button, TextField, Chip, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Backdrop, CircularProgress, Alert, Snackbar, IconButton,
  InputAdornment, FormControl, InputLabel, Select, MenuItem,
  SelectChangeEvent, Avatar, Badge, Divider, Tooltip,
  CardContent, LinearProgress, useTheme, alpha
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  MeetingRoom as RoomIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types
interface Assignment {
  id: number;
  assignmentDate: string;
  roomId: string;
  housekeeperId: string;
  supervisorId: string;
  status: 'assigned' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  business_id: string;
  branch_id: string;
  room_name?: string;
  housekeeper_name?: string;
  supervisor_name?: string;
}

interface FilterOptions {
  date: Date | null;
  status: string;
  search: string;
  housekeeperId: string;
}

interface StatsCard {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  trend?: number;
}

const AssignmentReport: React.FC = () => {
  const theme = useTheme();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Data stores
  const [rooms, setRooms] = useState<Record<string, string>>({});
  const [housekeepers, setHousekeepers] = useState<Record<string, string>>({});
  const [housekeeperList, setHousekeeperList] = useState<Array<{id: string, name: string}>>([]);
  
  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    date: null,
    status: 'all',
    search: '',
    housekeeperId: 'all'
  });
  
  const [showFilters, setShowFilters] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch rooms
  const fetchRooms = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      const response = await axios.post(`${baseUrl()}Housekeepers/getRoomsByBranch`, formData, config);
      
      if (response.data.status === "true" || response.data.status === true) {
        const roomsMap: Record<string, string> = {};
        response.data.data.forEach((room: any) => {
          roomsMap[room.product_id] = room.name;
        });
        setRooms(roomsMap);
      }
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  // Fetch housekeepers
  const fetchHousekeepers = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      const response = await axios.post(`${baseUrl()}Housekeepers/getHousekeepers`, formData, config);
      
      if (response.data.status) {
        const hkMap: Record<string, string> = {};
        const hkList: Array<{id: string, name: string}> = [];
        
        response.data.data.forEach((hk: any) => {
          const name = hk.fullName || `${hk.firstName} ${hk.lastName}`.trim() || hk.email || 'Unknown';
          hkMap[hk.user_id] = name;
          hkList.push({ id: hk.user_id, name });
        });
        
        setHousekeepers(hkMap);
        setHousekeeperList(hkList.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (error) {
      console.error("Error fetching housekeepers:", error);
    }
  };

  // Fetch assignments
  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      // Add single date filter if selected - FIXED DATE FORMATTING
      if (filters.date) {
        // Format the date as YYYY-MM-DD using local date parts to avoid timezone issues
        const year = filters.date.getFullYear();
        const month = String(filters.date.getMonth() + 1).padStart(2, '0');
        const day = String(filters.date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        formData.append("date", formattedDate);
      }
      
      if (filters.status !== 'all') {
        formData.append("status", filters.status);
      }
      if (filters.housekeeperId !== 'all') {
        formData.append("housekeeperId", filters.housekeeperId);
      }
      
      formData.append("page", (page + 1).toString());
      formData.append("limit", rowsPerPage.toString());

      const response = await axios.post(`${baseUrl()}Housekeepers/index`, formData, config);
      
      console.log('Assignments response:', response.data);

      if (response.data.status) {
        const assignmentsData = response.data.data?.assignments || [];
        setAssignments(assignmentsData);
        
        const enriched = assignmentsData.map((assignment: Assignment) => ({
          ...assignment,
          room_name: rooms[assignment.roomId] || `Room ${assignment.roomId.substring(0, 8)}...`,
          housekeeper_name: housekeepers[assignment.housekeeperId] || 'Unknown',
          supervisor_name: housekeepers[assignment.supervisorId] || 'Unknown'
        }));
        
        setFilteredAssignments(enriched);
        setTotalCount(assignmentsData.length);
      } else {
        showNotification('Failed to fetch assignments', 'error');
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showNotification('Error fetching assignment data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await Promise.all([fetchRooms(), fetchHousekeepers()]);
      setLoading(false);
    };
    loadInitialData();
  }, []);

  // Fetch when dependencies change - including single date
  useEffect(() => {
    if (Object.keys(rooms).length > 0 && Object.keys(housekeepers).length > 0) {
      fetchAssignments();
    }
  }, [page, rowsPerPage, filters.date, filters.status, filters.housekeeperId, rooms, housekeepers]);

  // Apply search filter locally
  useEffect(() => {
    if (!filters.search.trim() && filters.housekeeperId === 'all') {
      const enriched = assignments.map((assignment: Assignment) => ({
        ...assignment,
        room_name: rooms[assignment.roomId] || `Room ${assignment.roomId.substring(0, 8)}...`,
        housekeeper_name: housekeepers[assignment.housekeeperId] || 'Unknown',
        supervisor_name: housekeepers[assignment.supervisorId] || 'Unknown'
      }));
      setFilteredAssignments(enriched);
      return;
    }

    const searchTerm = filters.search.toLowerCase();
    
    const enriched = assignments.map((assignment: Assignment) => ({
      ...assignment,
      room_name: rooms[assignment.roomId] || `Room ${assignment.roomId.substring(0, 8)}...`,
      housekeeper_name: housekeepers[assignment.housekeeperId] || 'Unknown',
      supervisor_name: housekeepers[assignment.supervisorId] || 'Unknown'
    }));
    
    const filtered = enriched.filter((assignment) => {
      const matchesSearch = !filters.search.trim() || 
        assignment.room_name?.toLowerCase().includes(searchTerm) ||
        assignment.housekeeper_name?.toLowerCase().includes(searchTerm) ||
        assignment.supervisor_name?.toLowerCase().includes(searchTerm);
      
      const matchesHousekeeper = filters.housekeeperId === 'all' || 
        assignment.housekeeperId === filters.housekeeperId;
      
      return matchesSearch && matchesHousekeeper;
    });
    
    setFilteredAssignments(filtered);
  }, [filters.search, filters.housekeeperId, assignments, rooms, housekeepers]);

  // Calculate stats
  const getStats = (): StatsCard[] => {
    const total = assignments.length;
    const active = assignments.filter(a => a.status === 'assigned').length;
    const completed = assignments.filter(a => a.status === 'completed').length;
    const cancelled = assignments.filter(a => a.status === 'cancelled').length;
    
    return [
      {
        title: 'Total Assignments',
        value: total,
        icon: <AssignmentIcon />,
        color: theme.palette.primary.main,
        bgColor: alpha(theme.palette.primary.main, 0.1),
        trend: 12
      },
      {
        title: 'Active',
        value: active,
        icon: <PendingIcon />,
        color: theme.palette.warning.main,
        bgColor: alpha(theme.palette.warning.main, 0.1),
        trend: 5
      },
      {
        title: 'Completed',
        value: completed,
        icon: <CheckCircleIcon />,
        color: theme.palette.success.main,
        bgColor: alpha(theme.palette.success.main, 0.1),
        trend: -2
      },
      {
        title: 'Cancelled',
        value: cancelled,
        icon: <CancelIcon />,
        color: theme.palette.error.main,
        bgColor: alpha(theme.palette.error.main, 0.1),
        trend: -8
      }
    ];
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    handleFilterChange('status', event.target.value);
  };

  const handleHousekeeperChange = (event: SelectChangeEvent) => {
    handleFilterChange('housekeeperId', event.target.value);
  };

  // FIXED: Handle date change with timezone correction
  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Create a new date and set it to noon to avoid timezone issues
      const localDate = new Date(date);
      localDate.setHours(12, 0, 0, 0);
      handleFilterChange('date', localDate);
    } else {
      handleFilterChange('date', null);
    }
  };

  const clearFilters = () => {
    setFilters({
      date: null,
      status: 'all',
      search: '',
      housekeeperId: 'all'
    });
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned': return theme.palette.warning.main;
      case 'completed': return theme.palette.success.main;
      case 'cancelled': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'assigned': return 'Assigned';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const exportToCSV = () => {
    try {
      const headers = ['Room', 'Housekeeper', 'Supervisor', 'Status', 'Assignment Date', 'Created At', 'Last Updated'];
      const csvData = filteredAssignments.map(item => [
        item.room_name || `Room ${item.roomId.substring(0, 8)}...`,
        item.housekeeper_name || 'Unknown',
        item.supervisor_name || 'Unknown',
        getStatusLabel(item.status),
        item.assignmentDate,
        formatDate(item.createdAt),
        formatDate(item.updatedAt)
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `assignment_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Report exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Failed to export report', 'error');
    }
  };

  const stats = getStats();
  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  // FIXED: Format display date for label using local date methods
  const getDateLabel = () => {
    if (filters.date) {
      const month = filters.date.toLocaleString('default', { month: 'long' });
      const day = filters.date.getDate();
      const year = filters.date.getFullYear();
      return `Showing assignments for ${month} ${day}, ${year}`;
    }
    return "Showing today's assignments (default)";
  };

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ 
            backgroundColor: theme.palette.background.default,
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                bgcolor: 'background.paper',
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}
            >
              <Container maxWidth="xl">
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  justifyContent="space-between" 
                  alignItems={{ xs: 'start', sm: 'center' }}
                  spacing={2}
                  sx={{ py: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        width: 48,
                        height: 48
                      }}
                    >
                      <AssignmentIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={600}>
                        Assignment Report
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getDateLabel()}
                      </Typography>
                    </Box>
                  </Stack>
                  
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Export">
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={exportToCSV}
                          disabled={filteredAssignments.length === 0}
                          size="small"
                        >
                          Export
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Refresh">
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={fetchAssignments}
                        size="small"
                      >
                        Refresh
                      </Button>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {stats.map((stat, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                          borderColor: 'transparent'
                        }
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {stat.title}
                            </Typography>
                            <Typography variant="h4" fontWeight={700}>
                              {stat.value}
                            </Typography>
                          </Box>
                          <Avatar 
                            sx={{ 
                              bgcolor: stat.bgColor,
                              color: stat.color,
                              width: 48,
                              height: 48
                            }}
                          >
                            {stat.icon}
                          </Avatar>
                        </Stack>
                        {stat.trend && (
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                            <TrendingUpIcon 
                              sx={{ 
                                fontSize: 16, 
                                color: stat.trend > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }} 
                            />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: stat.trend > 0 ? theme.palette.success.main : theme.palette.error.main,
                                fontWeight: 600
                              }}
                            >
                              {Math.abs(stat.trend)}% vs last month
                            </Typography>
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Filters Section */}
              <Paper 
                elevation={0}
                sx={{ 
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  mb: 3,
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack 
                    direction="row" 
                    justifyContent="space-between" 
                    alignItems="center"
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge 
                        badgeContent={activeFilterCount} 
                        color="primary"
                        invisible={activeFilterCount === 0}
                      >
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                      endIcon={<FilterIcon />}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {/* Single Date Filter - Auto-submits on selection */}
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Select Date"
                            value={filters.date}
                            onChange={handleDateChange}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                                placeholder: 'Today (default)',
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <DateRangeIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                },
                              },
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={filters.status}
                              label="Status"
                              onChange={handleStatusChange}
                            >
                              <MenuItem value="all">All Statuses</MenuItem>
                              <MenuItem value="assigned">Assigned</MenuItem>
                              <MenuItem value="completed">Completed</MenuItem>
                              <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Housekeeper</InputLabel>
                            <Select
                              value={filters.housekeeperId}
                              label="Housekeeper"
                              onChange={handleHousekeeperChange}
                            >
                              <MenuItem value="all">All Housekeepers</MenuItem>
                              {housekeeperList.map(hk => (
                                <MenuItem key={hk.id} value={hk.id}>
                                  {hk.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search rooms or staff"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => handleFilterChange('search', '')}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                      
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {filters.date ? 'Data filtered by selected date' : 'Showing today\'s assignments by default'}
                        </Typography>
                        <Button 
                          variant="text" 
                          size="small" 
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All Filters
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              {/* Table */}
              <Paper 
                elevation={0}
                sx={{ 
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                {loading && <LinearProgress />}
                
                <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: theme.palette.background.paper }}>Room</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: theme.palette.background.paper }}>Housekeeper</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: theme.palette.background.paper }}>Supervisor</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: theme.palette.background.paper }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: theme.palette.background.paper }}>Assignment Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: theme.palette.background.paper }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: theme.palette.background.paper }}>Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAssignments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                            <Stack spacing={2} alignItems="center">
                              <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                              <Typography color="text.secondary">
                                No assignments found for {filters.date ? filters.date.toLocaleDateString() : 'today'}
                              </Typography>
                              <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={clearFilters}
                              >
                                Clear Filters
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredAssignments
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((assignment) => (
                            <TableRow 
                              key={assignment.id} 
                              hover
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar 
                                    sx={{ 
                                      width: 32, 
                                      height: 32, 
                                      bgcolor: alpha(theme.palette.info.main, 0.1),
                                      color: theme.palette.info.main
                                    }}
                                  >
                                    <RoomIcon sx={{ fontSize: 18 }} />
                                  </Avatar>
                                  <Typography variant="body2" fontWeight={500}>
                                    {assignment.room_name || `Room ${assignment.roomId.substring(0, 8)}...`}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar 
                                    sx={{ 
                                      width: 32, 
                                      height: 32, 
                                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                                      color: theme.palette.warning.main
                                    }}
                                  >
                                    <PersonIcon sx={{ fontSize: 18 }} />
                                  </Avatar>
                                  <Typography variant="body2">
                                    {assignment.housekeeper_name || 'Unknown'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {assignment.supervisor_name || 'Unknown'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={getStatusLabel(assignment.status)}
                                  size="small"
                                  sx={{ 
                                    bgcolor: alpha(getStatusColor(assignment.status), 0.1),
                                    color: getStatusColor(assignment.status),
                                    fontWeight: 600,
                                    borderRadius: 1
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {assignment.assignmentDate || formatDate(assignment.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(assignment.createdAt)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(assignment.updatedAt)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Divider />
                
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Container>

            {/* Notifications */}
            <Snackbar 
              open={snackbar.open} 
              autoHideDuration={6000} 
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert 
                onClose={handleCloseSnackbar} 
                severity={snackbar.severity} 
                variant="filled"
                elevation={6}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>

            {/* Loading Overlay */}
            <Backdrop 
              sx={{ 
                color: '#fff', 
                zIndex: theme.zIndex.drawer + 1,
                backdropFilter: 'blur(3px)'
              }} 
              open={loading && filteredAssignments.length === 0}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress color="inherit" />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Loading assignments...
                </Typography>
              </Stack>
            </Backdrop>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default AssignmentReport;